import alt from 'components/Dispatcher';
import immutable from 'immutable';
import immutableStore from 'alt-utils/lib/ImmutableUtil';

import FileUploadNewCallActions from 'actions/file-upload/FileUploadNewCallActions';
import FileUploadNewCallSource from 'sources/file-upload/FileUploadNewCallSource';

/**
 * FileUploadNewCallStore stores the state for the current active file upload on the client-side.
 * It's used only by the client-side to keep track of the progress of an active file upload
 */
@immutableStore
class FileUploadNewCallStore {
  constructor() {
    this.state = immutable.Map({
      closeNewCallDialog: false,
      uploadId: null,
      // error message when file upload fails
      uploadError: null,
      uploadFilename: null,
      // progress of file upload
      uploadProgress: 0.0,
      // set to true when the file and the metadata are uploaded
      uploadSuccess: null,
    });
    this.bindActions(FileUploadNewCallActions);
    this.registerAsync(FileUploadNewCallSource);
  }

  /**
   * The user closes the file upload dialog so we should clear the state including `uploadId`
   * and `uploadFilename` so that the next time the file upload dialog opens it shows a dropzone
   */
  onClearState() {
    const state = this.stateWithClearedValues(this.state);
    this.setState(state);
  }

  /**
   * Clear this.state.get('uploadError')
   */
  onClearUploadErrorAndProgress() {
    let state = this.state.set('uploadError', null);
    state = state.set('uploadProgress', 0.0);
    this.setState(state);
  }

  /**
   * Update this.state.get('closeNewCallDialog')
   */
  onUpdateCloseNewCallDialog(closed) {
    const state = this.state.set('closeNewCallDialog', closed);
    this.setState(state);
  }

  /**
   * Callback triggered when `FileUploadNewCallActions.updateFileUploadMetadata` is called, i.e.,
   * when the user wants to update the metadata for an uploaded file
   * @param  {array} args An array of arguments to this action
   */
  onUpdateFileUploadMetadata(args) {
    const id = args[0];
    const title = args[1];
    const contact = args[2];
    const original_filename = args[3];
    const isSingleSpeakerCall = args[4];
    setImmediate(() => {
      this.getInstance().updateFileMetadata(
        id,
        title,
        contact,
        original_filename,
        isSingleSpeakerCall
      );
    });
  }

  /**
   * Close <FileUploadNewCallDialog /> and clear every value in this.state in order to make the
   * upload dialog ready for another one
   */
  onUpdateFileUploadMetadataSuccess(data) {
    const state = this.state.set('uploadSuccess', true);
    this.setState(state);
  }

  /**
   * Return an immutable.Map object all of whose keys are cleared
   * @param  {immutable.Map} state An immutable.Map object whose states should be cleared
   *                               / made back to the default values
   * @return {immutable.Map}       An immutable.Map object whose states are cleared
   */
  stateWithClearedValues(state) {
    state = state.set('uploadId', null);
    state = state.set('uploadError', null);
    state = state.set('uploadFilename', null);
    state = state.set('uploadProgress', 0.0);
    state = state.set('uploadSuccess', null);
    return state;
  }

  /**
   * Callback triggered when `FIleUploadNewCallActions.uploadFile` is called, i.e.
   * when the user drops a file into the <dropzone /> component
   * @param  {File} file A file object
   */
  onUploadFile(file) {
    setImmediate(() => {
      this.getInstance().uploadFile(file);
    });
  }

  /**
   * Update `this.state.get('uploadProgress')` so that <FileUploadNewCallDialog /> can update and
   * transition it's UI immediately
   * @param  {float} progress The progress of the upload which should be a float number between
   *                          0.0 and 1.0, all-inclusive
   */
  onUploadFileInProgress(progress) {
    const state = this.state.set('uploadProgress', progress);
    this.setState(state);
  }

  /**
   * Update `this.state.get('uploadError')` so that the client-side user sees what kind of error the upload
   * might be having
   * @param  {object} resp A server-side response object
   */
  onUploadFileError(resp) {
    const status = resp.response.status;
    if (status === 413) {
      const error =
        'Your file is larger than 500MB; the upload feature is unable to handle files larger than that. Please talk to your account executive to learn how to automate file uploads.';
      const state = this.state.set('uploadError', error);
      this.setState(state);
    }
  }

  /**
   * Update `this.state.get('uploadId')`, `this.state.get('uploadProgress')` and `this.state.get('uploadFilename')`
   * @param  {object} resp A server-side response object
   */
  onUploadFileSuccess(resp) {
    const id = resp.data.id;
    const filename = resp.data.filename;
    let state = this.state.set('uploadId', id);
    state = state.set('uploadFilename', filename);
    state = state.set('uploadProgress', 1.0);
    this.setState(state);
  }
}

export default alt.createStore(FileUploadNewCallStore, 'FileUploadNewCallStore');
